import { useEffect, useMemo, useState } from 'react';
import { Combobox, type ComboboxOption } from '../../../ds/components';
import { useContactSearch } from '../api';

export interface ContactPickerProps {
  value: ComboboxOption | null;
  onChange: (option: ComboboxOption | null) => void;
  error?: string | undefined;
  autoFocus?: boolean | undefined;
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** „Kontakt" combobox over `GET /contacts?q=` (name, e-mail, phone; the server applies the visibility scope). */
export function ContactPicker({ value, onChange, error, autoFocus }: ContactPickerProps) {
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 250);
  const search = useContactSearch(debounced);
  const options: ComboboxOption[] = useMemo(() => {
    const hits: ComboboxOption[] = (search.data?.items ?? []).map((hit) => ({
      id: hit.id,
      label: `${hit.firstName} ${hit.lastName}`.trim(),
      description: hit.lifecycle.label,
    }));
    if (value && !hits.some((hit) => hit.id === value.id)) hits.unshift(value);
    return hits;
  }, [search.data, value]);
  return (
    <Combobox
      label="Kontakt"
      required
      options={options}
      value={value?.id ?? null}
      onChange={(_, option) => onChange(option)}
      onQueryChange={setQuery}
      filter={false}
      placeholder="Name, E-Mail oder Telefon"
      emptyText={query.trim().length < 2 ? 'Mindestens zwei Zeichen eingeben' : 'Kein Treffer'}
      error={error}
      autoFocus={autoFocus}
    />
  );
}
